import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="page-content min-h-screen flex items-center justify-center px-4 bg-[#FDFBEC]">
      <div className="pattern-bg" />
      <div className="neo-card w-full max-w-md p-8 bg-white relative z-10" data-testid="login-form">
        <div className="text-center mb-8">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/ebea1ea5-9731-4354-90db-f3840d8f2692/images/80514a52e5403549c758b4eb7a39bf04c9d4fbd1f3d392449159024de0890e38.png"
            alt="Retro Playground TV"
            className="w-14 h-14 mx-auto mb-3"
          />
          <h1 className="font-['Fredoka'] text-3xl font-black text-[#0A0A0A]">Welcome Back</h1>
          <p className="text-sm text-[#4B5563] font-['Nunito'] mt-1">Sign in to your Retro Playground TV account</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg border-2 border-[#FF007F] bg-[#FF007F]/10 text-sm text-[#FF007F] font-bold" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-lg font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
              placeholder="you@example.com"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 pr-10 border-2 border-[#0A0A0A] rounded-lg font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
                placeholder="Enter your password"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
                data-testid="toggle-password-btn"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="neo-btn w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#FF3860] text-white text-base disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            <LogIn size={18} strokeWidth={2.5} /> {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-sm text-[#4B5563] font-['Nunito'] mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="font-bold text-[#FF3860] hover:underline" data-testid="register-link">
            Sign Up
          </Link>
        </p>
      </div>
    </div>
  );
}
